import type { SiteLanguageCode } from "@/lib/language";

type Dict<T> = Record<SiteLanguageCode, T>;

type NavPopupItemCopy = {
  title: string;
  description: string;
};

type NavPopupSectionCopy = {
  title: string;
  items: NavPopupItemCopy[];
};

export const navPopupCopy: Dict<{
  hero: {
    services: { title: string; description: string; cta: string };
    products: { title: string; description: string; cta: string };
  };
  sidebar: {
    services: [string, string, string];
    products: [string, string, string];
  };
  services: NavPopupSectionCopy[];
  products: NavPopupSectionCopy[];
}> = {
  en: {
    hero: {
      services: {
        title: "DevDotCom - We build what ships",
        description:
          "DevDotCom engineers reliable web and mobile products, platform integrations, and developer-facing tools that help teams move faster and focus on outcomes.",
        cta: "All Services",
      },
      products: {
        title: "Our Products",
        description:
          "Discover our range of digital products, templates, and tools designed to accelerate your development workflow.",
        cta: "View All Products",
      },
    },
    sidebar: {
      services: [
        "Product Engineering",
        "Platform & Integrations",
        "Developer Tools",
      ],
      products: ["All Products", "Open Source", "Premium"],
    },
    services: [
      {
        title: "Web & App Development",
        items: [
          {
            title: "Web Development",
            description:
              "Custom websites and web applications built with modern technologies",
          },
          {
            title: "Full Stack Web Apps",
            description:
              "Complete web applications with frontend and backend solutions",
          },
          {
            title: "Website Customization",
            description:
              "Tailored modifications and enhancements for existing websites",
          },
          {
            title: "Android App Development",
            description:
              "Native and cross-platform mobile apps for Android devices",
          },
          {
            title: "APIs & Integrations",
            description: "Robust backend services and third-party integrations",
          },
        ],
      },
      {
        title: "AI & Automation",
        items: [
          {
            title: "AI / ML Solutions",
            description:
              "Custom artificial intelligence and machine learning implementations",
          },
          {
            title: "AI Technology Consulting",
            description: "Expert guidance on AI strategy and implementation",
          },
          {
            title: "AI Agents",
            description: "Intelligent autonomous systems for workflow automation",
          },
          {
            title: "AI Chatbot",
            description: "Conversational AI powered by advanced language models",
          },
          {
            title: "Automations & Agents",
            description:
              "Streamline business processes with intelligent automation",
          },
          {
            title: "NLP",
            description:
              "Natural language processing and understanding solutions",
          },
        ],
      },
      {
        title: "DevOps & Cloud",
        items: [
          {
            title: "DevOps & Cloud",
            description:
              "Infrastructure automation and cloud deployment solutions",
          },
          {
            title: "Speed Optimization",
            description:
              "Performance tuning and optimization for faster applications",
          },
        ],
      },
      {
        title: "Support & Maintenance",
        items: [
          {
            title: "Maintenance & Support",
            description: "Ongoing support and maintenance for your applications",
          },
          {
            title: "Bug Fixes",
            description: "Quick identification and resolution of software issues",
          },
          {
            title: "Website Customization",
            description: "Tailored modifications and feature enhancements",
          },
          {
            title: "Performance Optimization",
            description:
              "Speed and efficiency improvements for better user experience",
          },
        ],
      },
    ],
    products: [
      {
        title: "Digital Products",
        items: [
          {
            title: "SaaS Platforms",
            description:
              "Ready-to-use software solutions for common business needs",
          },
          {
            title: "WordPress Themes",
            description:
              "Professional themes for blogs, business sites, and e-commerce",
          },
          {
            title: "Code Libraries",
            description: "Open-source tools and libraries for developers",
          },
        ],
      },
      {
        title: "Resources",
        items: [
          {
            title: "Templates",
            description: "Professional templates for various industries",
          },
          {
            title: "Web Tools",
            description: "Useful online tools for developers and businesses",
          },
        ],
      },
    ],
  },
  hi: {
    hero: {
      services: {
        title: "DevDotCom - जो शिप होता है, वही बनाते हैं",
        description:
          "DevDotCom विश्वसनीय वेब और मोबाइल प्रोडक्ट, प्लेटफ़ॉर्म इंटीग्रेशन और डेवलपर टूल्स बनाता है - ताकि टीमें तेज़ी से आगे बढ़ें और परिणामों पर ध्यान दें।",
        cta: "सभी सेवाएँ",
      },
      products: {
        title: "हमारे उत्पाद",
        description:
          "डिजिटल प्रोडक्ट, टेम्प्लेट और टूल्स की श्रृंखला खोजें - आपके डेवलपमेंट वर्कफ़्लो को तेज़ करने के लिए।",
        cta: "सभी उत्पाद देखें",
      },
    },
    sidebar: {
      services: [
        "प्रोडक्ट इंजीनियरिंग",
        "प्लेटफ़ॉर्म और इंटीग्रेशन",
        "डेवलपर टूल्स",
      ],
      products: ["सभी उत्पाद", "ओपन सोर्स", "प्रीमियम"],
    },
    services: [
      {
        title: "वेब और ऐप डेवलपमेंट",
        items: [
          {
            title: "वेब डेवलपमेंट",
            description:
              "आधुनिक तकनीकों से बने कस्टम वेबसाइट और वेब एप्लिकेशन",
          },
          {
            title: "फुल स्टैक वेब ऐप्स",
            description:
              "फ्रंटएंड और बैकएंड सहित संपूर्ण वेब एप्लिकेशन",
          },
          {
            title: "वेबसाइट कस्टमाइज़ेशन",
            description:
              "मौजूदा वेबसाइटों के लिए अनुकूलित बदलाव और सुधार",
          },
          {
            title: "एंड्रॉइड ऐप डेवलपमेंट",
            description:
              "एंड्रॉइड डिवाइसों के लिए नेटिव और क्रॉस-प्लेटफ़ॉर्म मोबाइल ऐप्स",
          },
          {
            title: "API और इंटीग्रेशन",
            description: "मज़बूत बैकएंड सेवाएँ और थर्ड-पार्टी इंटीग्रेशन",
          },
        ],
      },
      {
        title: "AI और ऑटोमेशन",
        items: [
          {
            title: "AI / ML समाधान",
            description:
              "कस्टम आर्टिफिशियल इंटेलिजेंस और मशीन लर्निंग इम्प्लीमेंटेशन",
          },
          {
            title: "AI टेक्नोलॉजी कंसल्टिंग",
            description: "AI रणनीति और इम्प्लीमेंटेशन पर विशेषज्ञ मार्गदर्शन",
          },
          {
            title: "AI एजेंट्स",
            description: "वर्कफ़्लो ऑटोमेशन के लिए बुद्धिमान स्वायत्त सिस्टम",
          },
          {
            title: "AI चैटबॉट",
            description: "उन्नत भाषा मॉडल पर आधारित संवादात्मक AI",
          },
          {
            title: "ऑटोमेशन और एजेंट्स",
            description:
              "बुद्धिमान ऑटोमेशन से व्यावसायिक प्रक्रियाओं को सरल बनाएँ",
          },
          {
            title: "NLP",
            description:
              "प्राकृतिक भाषा प्रसंस्करण और समझ के समाधान",
          },
        ],
      },
      {
        title: "DevOps और क्लाउड",
        items: [
          {
            title: "DevOps और क्लाउड",
            description:
              "इन्फ्रास्ट्रक्चर ऑटोमेशन और क्लाउड डिप्लॉयमेंट समाधान",
          },
          {
            title: "स्पीड ऑप्टिमाइज़ेशन",
            description:
              "तेज़ एप्लिकेशन के लिए परफ़ॉर्मेंस ट्यूनिंग और ऑप्टिमाइज़ेशन",
          },
        ],
      },
      {
        title: "सपोर्ट और मेंटनेंस",
        items: [
          {
            title: "मेंटनेंस और सपोर्ट",
            description: "आपके एप्लिकेशन के लिए निरंतर सपोर्ट और मेंटनेंस",
          },
          {
            title: "बग फिक्स",
            description: "सॉफ़्टवेयर समस्याओं की त्वरित पहचान और समाधान",
          },
          {
            title: "वेबसाइट कस्टमाइज़ेशन",
            description: "अनुकूलित बदलाव और फीचर एन्हांसमेंट",
          },
          {
            title: "परफ़ॉर्मेंस ऑप्टिमाइज़ेशन",
            description:
              "बेहतर उपयोगकर्ता अनुभव के लिए गति और दक्षता में सुधार",
          },
        ],
      },
    ],
    products: [
      {
        title: "डिजिटल उत्पाद",
        items: [
          {
            title: "SaaS प्लेटफ़ॉर्म",
            description:
              "सामान्य व्यावसायिक ज़रूरतों के लिए तैयार सॉफ़्टवेयर समाधान",
          },
          {
            title: "वर्डप्रेस थीम्स",
            description:
              "ब्लॉग, बिज़नेस साइट और ई-कॉमर्स के लिए पेशेवर थीम्स",
          },
          {
            title: "कोड लाइब्रेरीज़",
            description: "डेवलपर्स के लिए ओपन-सोर्स टूल्स और लाइब्रेरीज़",
          },
        ],
      },
      {
        title: "संसाधन",
        items: [
          {
            title: "टेम्प्लेट्स",
            description: "विभिन्न उद्योगों के लिए पेशेवर टेम्प्लेट",
          },
          {
            title: "वेब टूल्स",
            description: "डेवलपर्स और व्यवसायों के लिए उपयोगी ऑनलाइन टूल्स",
          },
        ],
      },
    ],
  },
} as const;

export const blogNavPopupCopy: Dict<{
  title: string;
  description: string;
  featured: string;
  minRead: string;
  viewAll: string;
  categories: string;
  quickLinks: string;
  quickLinkLabels: {
    allArticles: string;
    latestTech: string;
    developmentGuides: string;
    designInsights: string;
  };
}> = {
  en: {
    title: "DevDotCom Blog",
    description:
      "Insights, tutorials, and updates from our team of engineers and designers. Stay ahead with the latest in web development, AI, and cloud technologies.",
    featured: "Featured Articles",
    minRead: "min read",
    viewAll: "View All Articles",
    categories: "Categories",
    quickLinks: "Quick Links",
    quickLinkLabels: {
      allArticles: "All Articles",
      latestTech: "Latest Tech",
      developmentGuides: "Development Guides",
      designInsights: "Design Insights",
    },
  },
  hi: {
    title: "DevDotCom ब्लॉग",
    description:
      "हमारी इंजीनियर और डिज़ाइनर टीम से अंतर्दृष्टि, ट्यूटोरियल और अपडेट। वेब डेवलपमेंट, AI और क्लाउड तकनीक में आगे रहें।",
    featured: "विशेष लेख",
    minRead: "मिनट पढ़ें",
    viewAll: "सभी लेख देखें",
    categories: "श्रेणियाँ",
    quickLinks: "त्वरित लिंक",
    quickLinkLabels: {
      allArticles: "सभी लेख",
      latestTech: "नवीनतम टेक",
      developmentGuides: "डेवलपमेंट गाइड",
      designInsights: "डिज़ाइन इनसाइट्स",
    },
  },
} as const;
